/**
 * BillingCodesSection
 *
 * CPT codes, ICD-10 codes, suggested CPT codes, and psychotherapy minutes.
 */

import React from 'react';
import { CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { ICD10CodePicker } from '@components/ui/forms/ICD10CodePicker';
import { Stack, TextField } from '@mui/material';
import type {
  WorkingBillingClaimLineItemResponse4,
  WorkingClaimLineItem,
} from '@store/generated/encounters';

import type { EncounterAttestations } from '../../types/attestations';

interface BillingCodesSectionProps {
  cptCodes: string[];
  onCptCodesChange: (codes: string[]) => void;
  cptLineItems?: WorkingBillingClaimLineItemResponse4[];
  onCptLineItemsChange?: (items: WorkingClaimLineItem[]) => void;
  suggestedCptCodes?: string[];
  onUseSuggestedCptCodes?: () => void;
  /** Show inline modifier editing below each CPT code */
  showModifiers?: boolean;
  /** Claim-level ICD-10 codes for per-line diagnosis linking */
  claimDiagnosisCodes?: string[];

  icd10Codes: string[];
  onIcd10CodesChange: (codes: string[]) => void;
  /** Provenance for each diagnosis code */
  diagnosisCodeSources?: Record<string, 'provider_selected' | 'ai_recommended'>;

  // Psychotherapy minutes (shown when 2 CPT codes selected)
  psychotherapyMinutes?: number | null;
  onPsychotherapyMinutesChange?: (
    field: keyof EncounterAttestations,
    value: number | null,
  ) => void;

  editable?: boolean;
}

export const BillingCodesSection: React.FC<BillingCodesSectionProps> = ({
  cptCodes,
  onCptCodesChange,
  cptLineItems,
  onCptLineItemsChange,
  suggestedCptCodes,
  onUseSuggestedCptCodes,
  showModifiers,
  claimDiagnosisCodes,
  icd10Codes,
  onIcd10CodesChange,
  diagnosisCodeSources,
  psychotherapyMinutes,
  onPsychotherapyMinutesChange,
  editable = true,
}) => {
  const showPsychotherapyMinutes = cptCodes.length >= 2;

  return (
    <Stack spacing={2}>
      {/* CPT Codes */}
      <CPTCodePicker
        value={cptCodes}
        onChange={onCptCodesChange}
        onLineItemsChange={onCptLineItemsChange}
        lineItems={cptLineItems}
        suggestedCodes={suggestedCptCodes}
        onUseSuggestedCodes={onUseSuggestedCptCodes}
        showModifiers={showModifiers}
        claimDiagnosisCodes={claimDiagnosisCodes}
        showHeader={false}
        disabled={!editable}
      />

      {/* ICD-10 Codes */}
      <ICD10CodePicker
        value={icd10Codes}
        onChange={onIcd10CodesChange}
        diagnosisCodeSources={diagnosisCodeSources}
        showHeader={false}
        size="small"
        disabled={!editable}
      />

      {/* Psychotherapy Minutes — only when 2 CPT codes (E&M + psychotherapy) */}
      {showPsychotherapyMinutes && (
        <TextField
          type="number"
          label="Psychotherapy Minutes"
          value={psychotherapyMinutes ?? ''}
          onChange={e => {
            const val = e.target.value
              ? parseInt(e.target.value, 10)
              : null;
            onPsychotherapyMinutesChange?.('psychotherapy_minutes', val);
          }}
          fullWidth
          size="small"
          disabled={!editable}
         slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: 0 } }}/>
      )}
    </Stack>
  );
};
